import { useEffect, useRef } from 'react';
import './GameResultNotification.css';

interface GameResultNotificationProps {
  type: 'win' | 'lose';
  title: string;
  message: string;
  /** Winning hand name, e.g. "Straight, Ten High" - shown big, never cut. */
  handName?: string | null;
  /** Extra lines (split partners, side pots, chips returned). Wrapped, never truncated. */
  detailLines?: string[];
  durationMs?: number;
  onClose: () => void;
}

/**
 * The hand-result card. The old version was a 2.5-second toast that cut the
 * message at 40 characters - "(split with Silicon ..." - which on a phone
 * meant nobody ever knew who won, with what, or why their stack changed.
 * This one anchors top-centre (bottom is owned by action buttons and ready
 * toasts on iOS), wraps instead of truncating, names the winning hand, and
 * stays up long enough to actually read.
 */
const GameResultNotification: React.FC<GameResultNotificationProps> = ({
  type,
  title,
  message,
  handName,
  detailLines,
  durationMs = 6500,
  onClose,
}) => {
  // Keep the latest onClose without restarting the dismiss timer when the
  // parent re-renders (a new closure every snapshot used to reset it).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const timer = setTimeout(() => onCloseRef.current(), durationMs);
    return () => clearTimeout(timer);
  }, [durationMs]);

  return (
    <div
      className={`game-notification game-notification-${type}`}
      style={{ ['--gn-duration' as any]: `${durationMs}ms` }}
      onClick={onClose}
      role="status"
    >
      <div className="game-notification-content">
        <h4 className="game-notification-title">{title}</h4>
        {handName && <div className="game-notification-hand">{handName}</div>}
        <p className="game-notification-message">{message}</p>
        {(detailLines || []).map((line, i) => (
          <p className="game-notification-detail" key={i}>{line}</p>
        ))}
      </div>
      <button
        className="game-notification-close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close notification"
      >
        ×
      </button>
      <div className="game-notification-progress">
        <div className={`game-notification-progress-bar game-notification-progress-${type}`} />
      </div>
    </div>
  );
};

export default GameResultNotification;
